import { TypographyXS } from "@/components/typography/XS";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { generateSlugFromName } from "../../hooks/generateSlugFromName";
import { ProjectTemplate } from "../../types/app.type";
import { LocalAppProject } from "./LocalProject/LocalAppProject";
import { CustomSubmit } from "./LocalProject/types/customSubmit.type";

type Props = {
  selectedTemplate: ProjectTemplate;
  gitLinkError: string | null;
  nameError: string | null;
  urlError: string | null;
  setSubmit: React.Dispatch<React.SetStateAction<CustomSubmit | null>>;
};

const CreateAppDetails = ({
  selectedTemplate,
  gitLinkError,
  nameError,
  urlError,
  setSubmit
}: Props) => {
  const [slugInput, setSlugInput] = useState("");

  const handleGenerateSlug = (slug: string) => {
    const generatedSlug = generateSlugFromName(slug);
    setSlugInput(generatedSlug);
  };

  if (selectedTemplate.id === "local-project")
    return <LocalAppProject setSubmit={setSubmit} />;
  return (
    <div className="mt-4 grid items-center gap-2">
      <div className="grid grid-cols-[20%_1fr] items-center gap-x-4 gap-y-1">
        <Label
          htmlFor={
            selectedTemplate.id === "use-public-repo" ? "githubLink" : undefined
          }
          className="text-right text-brand-gray-700 dark:text-brand-gray-100"
        >
          GitHub
        </Label>
        {selectedTemplate.id === "use-public-repo" ? (
          <>
            <Input
              id="githubLink"
              name="githubLink"
              type="text"
              placeholder="https://github.com/owner/your-public-repository"
              className="dark:border-brand-gray-700"
              required
            />
            {gitLinkError && (
              <TypographyXS className="col-start-2 ml-2 text-brand-error dark:text-brand-error font-normal tracking-wide">
                {gitLinkError}
              </TypographyXS>
            )}
          </>
        ) : (
          <Dialog.Description className="h-9 ml-2 flex items-center text-brand-gray-700 dark:text-brand-gray-300">
            {selectedTemplate.githubLink ? (
              <>
                See the
                <Button variant="link" size="inline">
                  <a
                    data-testid=""
                    href={selectedTemplate.githubLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    repository
                    <ExternalLink size={12} className="ml-1 inline" />
                  </a>
                </Button>
                for{" "}
                <span className="ml-1 font-medium">
                  {selectedTemplate.name}.
                </span>
              </>
            ) : (
              <span className="text-sm text-brand-gray-400 dark:text-brand-gray-300 font-light tracking-wide italic">
                No link available.
              </span>
            )}
          </Dialog.Description>
        )}
      </div>
      <div className="grid grid-cols-[20%_1fr] items-center gap-x-4 gap-y-1">
        <Label
          htmlFor="appName"
          className="text-right text-brand-gray-700 dark:text-brand-gray-100"
        >
          App name
        </Label>
        <Input
          id="appName"
          name="appName"
          type="text"
          placeholder="Name"
          className="dark:border-brand-gray-700"
          required
          onChange={(e) => handleGenerateSlug(e.target.value)}
        />
        {nameError && (
          <TypographyXS className="col-start-2 ml-2 text-brand-error dark:text-brand-error font-normal tracking-wide">
            {nameError}
          </TypographyXS>
        )}
      </div>
      <div className="grid grid-cols-[20%_1fr] items-center gap-4">
        <Label
          htmlFor="appDescription"
          className="text-right text-brand-gray-700 dark:text-brand-gray-100"
        >
          App description
        </Label>
        <Input
          id="appDescription"
          name="appDescription"
          type="text"
          placeholder="Description"
          className="dark:border-brand-gray-700"
        />
      </div>
      <div className="grid grid-cols-[20%_1fr] items-center gap-x-4 gap-y-1">
        <Label
          htmlFor="appSlug"
          className="text-right text-brand-gray-700 dark:text-brand-gray-100"
        >
          App slug
        </Label>
        <Input
          id="appSlug"
          name="appSlug"
          type="text"
          placeholder="slug-from-name"
          className="pt-1.5 dark:border-brand-gray-700 text-brand-gray-700 dark:text-brand-gray-100"
          required
          value={slugInput}
          onChange={(e) => handleGenerateSlug(e.target.value)}
        />
        <TypographyXS className="col-start-2 ml-2 text-brand-gray-400 dark:text-brand-gray-300 font-light tracking-wider italic">
          &quot;a-z&quot; &quot;-&quot; &quot;0-9&quot;
        </TypographyXS>
        {urlError && (
          <TypographyXS className="col-start-2 ml-2 text-brand-error dark:text-brand-error font-normal tracking-wide">
            {urlError}
          </TypographyXS>
        )}
      </div>
    </div>
  );
};

export default CreateAppDetails;
